/**
 * Created by Ning Jiang on 10/11/2018.
 */

import {CountDownTimer} from "@nolimitcity/slot-game/bin/core/component/CountDownTimer";
import {NineSliceButton} from "@nolimitcity/slot-game/bin/core/component/NineSliceButton";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IScreenViewConfig, ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Back, TimelineLite, TweenLite} from "gsap";
import {ShootingAnimation} from "../../winpresentation/ShootingAnimation";
import {TombstoneGameModuleConfig} from "../../TombstoneGameModuleConfig";

export interface ITombstoneFreespinIntroViewConfig extends IScreenViewConfig {
    featureNameTexture:string,
    amountTexture:string,
}

export class TombstoneFreespinIntroView extends ScreenView {

    private readonly _sparkPositions:[number, number][] = [[-100, -122], [-86, -56], [122, -102]];
    private readonly _sparkDelays:number[] = [0.6, 0.9, 1.1];

    private _tombstoneConfig:ITombstoneFreespinIntroViewConfig;
    private _container:PIXI.Container;
    private _darkOverlay:PIXI.Graphics;
    private _rays:TimelineSprite;
    private _number:PIXI.Sprite;
    private _featureName:TimelineSprite;
    private _spinsName:TimelineSprite;
    private _playSoundCallback:() => void;

    private _timeline:TimelineLite;
    private _restTimeline:TimelineLite;

    constructor(config:ITombstoneFreespinIntroViewConfig, autoClose:boolean = false) {
        super(config, true);
        this._tombstoneConfig = config;
    }

    protected createGameGraphics():PIXI.DisplayObject {
        const container:PIXI.Container = new PIXI.Container();

        this._darkOverlay = new PIXI.Graphics;
        container.addChild(this._darkOverlay);

        this._rays = new TimelineSprite(GameResources.getTextures("bigWinRays"));
        this._rays.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._rays.anchor.set(0.5);
        container.addChild(this._rays);

        this._featureName = new TimelineSprite(GameResources.getTextures(this._tombstoneConfig.featureNameTexture));
        this._featureName.anchor.set(0.5);
        this._featureName.position.set(0, 145 - 360);
        this._featureName.hide();
        container.addChild(this._featureName);

        this._number = new PIXI.Sprite(GameResources.getTextures(this._tombstoneConfig.amountTexture)[0]);
        this._number.anchor.set(0.5);
        this._number.position.set(0, -60);
        container.addChild(this._number);

        this._spinsName = new TimelineSprite(GameResources.getTextures("introTextSpins"));
        this._spinsName.anchor.set(0.5);
        this._spinsName.position.set(0, 533 - 360);
        this._spinsName.hide();
        container.addChild(this._spinsName);

        this._container = container;
        return container;
    }

    protected createButton():NineSliceButton {
        const button:NineSliceButton = super.createButton();
        button.visible = false;
        return button;
    }

    protected createAutoCloseTimer():CountDownTimer {
        const countDown:CountDownTimer = super.createAutoCloseTimer();
        countDown.visible = false;
        return countDown;
    }

    protected onResizeGameGraphics(resizeData:IResizeData):void {
        this._container.position.set(this._screenWidth * 0.5, this._screenHeight * 0.5);

        this._darkOverlay.clear();
        this._darkOverlay.beginFill(0x000000, 0.8);
        this._darkOverlay.drawRect(-this._screenWidth / 2, -this._screenHeight / 2, this._screenWidth, this._screenHeight);
    }

    protected startPresentation(data?:ParsedServerData, onShowComplete?:() => void):void {
        if(!this._timeline) {
            const timeline:TimelineLite = new TimelineLite({paused : true});
            const resetTimeline:TimelineLite = new TimelineLite({paused : true});
            timeline.add([
                () => {
                    TombstoneGameModuleConfig.TBBWController.hideCountUp();
                },
                TweenLite.fromTo(this._container, 0.1, {alpha : 0}, {alpha : 1}),
                TweenLite.fromTo(this._rays.scale, 0.6, {x : 1, y : 1}, {x : 1.5, y : 1.5}),
                TweenLite.fromTo(this._number, 0.55, {alpha : 0}, {alpha : 1}),
                TweenLite.fromTo(this._number.scale, 0.55,
                    {x : 2, y : 2},
                    {x : 1, y : 1, ease : Back.easeIn.config(1.4)}),
                () => this._rays.playLoop()
            ]);
            timeline.add(() => this.playSound(), 0.54);
            timeline.add(this._featureName.getAnimationAutoShowHide(true, false), 0.2);
            timeline.add(this._spinsName.getAnimationAutoShowHide(true, false), 0.53);
            this._sparkPositions.forEach((positions:[number, number], index:number) => {
                const shootingAnimation:ShootingAnimation = new ShootingAnimation();
                shootingAnimation.createAnimation(this._container, this._container, positions, positions);
                timeline.add(shootingAnimation, this._sparkDelays[index]);
                resetTimeline.add(() => shootingAnimation.reset(), 0.2);
            });

            timeline.add(() => {
                this._button.visible = true;
                // this._autoCloseTimer.visible = true;
                if(onShowComplete) {
                    onShowComplete();
                }
            });

            resetTimeline.add(TweenLite.to(this._container, 0.2, {alpha : 0}), 0);

            this._timeline = timeline;
            this._restTimeline = resetTimeline;
        }
        this._timeline.play(0);
    }

    protected stopPresentation(closeCallback?:() => void):void {
        this._rays.stopLoop();
        this._featureName.hide();
        this._spinsName.hide();
        this._restTimeline.play(0);
        this._button.visible = false;
        // this._autoCloseTimer.visible = false;

        super.stopPresentation(closeCallback);
    }

    public getPlaySoundCallback(playSoundCallback:() => void):void {
        this._playSoundCallback = playSoundCallback;
    }

    private playSound():void {
        if(this._playSoundCallback) {
            this._playSoundCallback();
        }
    }
}